import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { PickListContext } from './Context';
import type { BaseGroup, BaseItem, PickListContextValue } from './type';

const PickListProvider = <
  I extends BaseItem = BaseItem,
  G extends BaseGroup<I> = BaseGroup<I>,
>({
  groupList,
  value: pickedItemList,
  renderItem,
  onChange,
  children,
  allItems,
  allGroupText,
}: {
  groupList: G[];
  value: I[];
  allGroupText?: string;
  renderItem: (itemList: I) => ReactNode;
  onChange?: (items: I[]) => void;
  children: ReactNode;
  allItems: I[];
}) => {
  const allItemsGroup: G = useMemo(() => {
    return {
      id: -1,
      name: `${allGroupText ?? 'All'}`,
      itemList: allItems,
    } as G;
  }, [allGroupText, allItems]);

  const [selectedGroup, setSelectedGroup] = useState<G>(allItemsGroup);
  const [innerPickedItems, setInnerPickedItems] = useState<I[]>(pickedItemList);

  const isItemSelected = (item: I) =>
    innerPickedItems.some(i => i.value === item.value);

  const isGroupAllSelected = (group: G) =>
    !group.itemList.some(item => !isItemSelected(item));

  const toggleItem = (item: I) => {
    setInnerPickedItems(prev =>
      isItemSelected(item)
        ? prev.filter(i => i.value !== item.value)
        : [...prev, item],
    );
  };

  const toggleGroup = () => {
    const group = selectedGroup;

    if (isGroupAllSelected(group)) {
      const next = innerPickedItems.filter(
        inner => !group.itemList.find(gi => gi.value === inner.value),
      );
      setInnerPickedItems(next);
      return;
    }

    const next = group.itemList.filter(
      gi => !innerPickedItems.find(inner => inner.value === gi.value),
    );
    setInnerPickedItems([...innerPickedItems, ...next]);
  };

  const selectGroup = (group: G | null | undefined) => {
    setSelectedGroup(group ?? allItemsGroup);
  };

  useEffect(() => {
    onChange?.(innerPickedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerPickedItems]);

  const value: PickListContextValue<I, G> = {
    groupList,
    selectedGroup,
    innerPickedItems,
    isItemSelected,
    isGroupAllSelected,
    toggleItem,
    toggleGroup,
    selectGroup,
    allItemsGroup,
    renderItem,
  };

  return (
    <PickListContext.Provider
      value={
        value as unknown as PickListContextValue<BaseItem, BaseGroup<BaseItem>>
      }
    >
      {children}
    </PickListContext.Provider>
  );
};

export default PickListProvider;
